import { createContext, useContext, useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, ShoppingCart, Factory, Printer, Package, FolderTree, Warehouse, Cylinder, Users, Wallet, Ticket, Star, Truck, Image, BarChart3, UserCog, Settings, ScrollText, LogOut, ChevronsLeft, Bell, FileQuestion, ShoppingBag, Newspaper, Menu, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, dt } from "@/lib/api";
import { useFetch } from "./ui";

const Ctx = createContext(null);
export const useAdmin = () => useContext(Ctx);

export const MENU = [
  ["dashboard", "Dashboard", LayoutDashboard, ""], ["pedidos", "Pedidos", ShoppingCart, "pedidos"], ["producao", "Produção", Factory, "producao"],
  ["maquinas", "Máquinas", Printer, "maquinas"], ["produtos", "Produtos", Package, "produtos"], ["categorias", "Categorias", FolderTree, "categorias"],
  ["estoque", "Estoque", Warehouse, "estoque"], ["filamentos", "Filamentos", Cylinder, "filamentos"], ["clientes", "Clientes", Users, "clientes"],
  ["financeiro", "Financeiro", Wallet, "financeiro"], ["cupons", "Cupons", Ticket, "cupons"], ["avaliacoes", "Avaliações", Star, "avaliacoes"],
  ["fretes", "Fretes", Truck, "fretes"], ["banners", "Banners", Image, "banners"], ["orcamentos", "Orçamentos", FileQuestion, "orcamentos"],
  ["carrinhos", "Carrinhos", ShoppingBag, "carrinhos"], ["blog", "Blog", Newspaper, "blog"], ["relatorios", "Relatórios", BarChart3, "relatorios"],
  ["usuarios", "Usuários", UserCog, "usuarios"], ["configuracoes", "Configurações", Settings, "configuracoes"], ["logs", "Logs", ScrollText, "logs"],
];
const PERM_ALIAS = { fretes: "configuracoes" };
export const canAccess = (u, mod) => u?.permissions === "*" || (u?.permissions || []).includes(PERM_ALIAS[mod] || mod);

function Bellbox() {
  const [list, reload] = useFetch("/admin/notifications");
  const [open, setOpen] = useState(false);
  const unread = (list || []).filter((n) => !n.read).length;
  useEffect(() => { const t = setInterval(reload, 30000); return () => clearInterval(t); }, [reload]);
  const markRead = async () => { await api.post("/admin/notifications/read"); reload(); };
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="relative h-10 w-10 grid place-items-center rounded-xl hover:bg-[#171A1F]" aria-label="Notificações" data-testid="admin-notifications-btn">
        <Bell className="h-5 w-5" />{unread > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-5 h-5 rounded-full bg-[#008CFF] text-[10px] font-bold grid place-items-center">{unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 card p-2 z-50 max-h-96 overflow-y-auto shadow-2xl" data-testid="admin-notifications-list">
          <div className="flex justify-between items-center px-2 py-1"><b className="text-sm">Notificações</b><button className="text-xs text-[#00C8FF]" onClick={markRead}>Marcar lidas</button></div>
          {(list || []).map((n) => <Link key={n.id} to={n.link || "#"} onClick={() => setOpen(false)} className={`block p-2 rounded-lg text-sm hover:bg-[#1f232b] ${n.read ? "text-[#A0A0A0]" : ""}`}>{n.title}<div className="text-[10px] text-[#6b7079]">{dt(n.created_at)}</div></Link>)}
        </div>
      )}
    </div>
  );
}

export default function AdminLayout() {
  const [user, setUser] = useState(undefined);
  const [collapsed, setCollapsed] = useState(localStorage.getItem("e3_sidebar") === "1");
  const [mobile, setMobile] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();
  useEffect(() => { api.get("/admin/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null)); }, []);
  useEffect(() => { setMobile(false); }, [loc.pathname]);
  if (user === undefined) return <div className="min-h-screen" />;
  if (!user) return <Navigate to={`/administrativo/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  const logout = async () => { await api.post("/admin/auth/logout"); toast.success("Sessão encerrada"); nav("/administrativo/login"); };
  const toggle = () => { localStorage.setItem("e3_sidebar", collapsed ? "0" : "1"); setCollapsed(!collapsed); };
  const items = MENU.filter(([m]) => canAccess(user, m));
  const side = (
    <nav className="flex flex-col gap-0.5 p-3 flex-1 overflow-y-auto scrollbar-none">
      {items.map(([m, label, I, path]) => (
        <NavLink key={m} to={`/administrativo${path ? `/${path}` : ""}`} end={!path} title={label}
          className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[#008CFF]/12 text-[#00C8FF]" : "text-[#A0A0A0] hover:text-white hover:bg-[#171A1F]"}`} data-testid={`admin-nav-${m}`}>
          <I className="h-4 w-4 shrink-0" />{!collapsed && <span className="truncate">{label}</span>}
        </NavLink>
      ))}
      <button onClick={logout} className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-[#A0A0A0] hover:text-red-400 mt-2" data-testid="admin-logout"><LogOut className="h-4 w-4" />{!collapsed && "Sair"}</button>
    </nav>
  );
  return (
    <Ctx.Provider value={{ user }}>
      <div className="min-h-screen flex bg-[#07080B]">
        <aside className={`no-print hidden lg:flex flex-col sticky top-0 h-screen border-r border-[#171A1F] bg-[#050608] transition-[width] duration-300 ${collapsed ? "w-[72px]" : "w-60"}`} data-testid="admin-sidebar">
          <div className="h-16 flex items-center justify-between px-4 border-b border-[#171A1F]">
            {!collapsed && <img src="/brand/logo-sm.png" alt="E³" className="h-9" />}
            <button onClick={toggle} aria-label="Recolher menu" className="p-1.5 rounded-lg hover:bg-[#171A1F]" data-testid="admin-sidebar-toggle"><ChevronsLeft className={`h-4 w-4 transition-transform ${collapsed ? "rotate-180" : ""}`} /></button>
          </div>
          {side}
        </aside>
        {mobile && <div className="fixed inset-0 z-50 lg:hidden"><div className="absolute inset-0 bg-black/70" onClick={() => setMobile(false)} /><aside className="absolute left-0 top-0 h-full w-64 bg-[#050608] border-r border-[#171A1F] flex flex-col">{side}</aside></div>}
        <div className="flex-1 min-w-0 flex flex-col">
          <header className="no-print h-16 sticky top-0 z-30 flex items-center gap-3 px-4 lg:px-8 border-b border-[#171A1F] bg-[#07080B]/90 backdrop-blur">
            <button className="lg:hidden p-2" onClick={() => setMobile(true)} aria-label="Menu" data-testid="admin-mobile-menu"><Menu className="h-5 w-5" /></button>
            <span className="text-xs chip bg-[#171A1F] text-[#A0A0A0] uppercase">{user.role.replace("_", " ")}</span>
            <div className="ml-auto flex items-center gap-2">
              <a href="/" target="_blank" rel="noreferrer" className="hidden sm:flex items-center gap-1 text-xs text-[#A0A0A0] hover:text-white"><ExternalLink className="h-3.5 w-3.5" />Ver loja</a>
              <Bellbox />
              <span className="text-sm font-semibold hidden sm:block" data-testid="admin-user-name">{user.name}</span>
            </div>
          </header>
          <main className="p-4 lg:p-8 min-w-0"><Outlet /></main>
        </div>
      </div>
    </Ctx.Provider>
  );
}

export function Guard({ mod, children }) {
  const { user } = useAdmin();
  if (!canAccess(user, mod)) return <div className="card p-10 text-center" data-testid="admin-forbidden"><h2 className="text-xl font-semibold">Acesso restrito</h2><p className="text-sm text-[#A0A0A0] mt-2">Seu perfil não tem permissão para este módulo.</p></div>;
  return children;
}

export function AdminLogin() {
  const [f, setF] = useState({});
  const [forgot, setForgot] = useState(false);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (forgot) { await api.post("/admin/auth/forgot-password", { email: f.email, origin: window.location.origin }); toast.success("Se o e-mail existir, enviaremos o link."); setForgot(false); }
      else { await api.post("/admin/auth/login", f); nav(new URLSearchParams(loc.search).get("next") || "/administrativo"); }
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden"><img src="/demo/farm.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" /><div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#050608]" /><div className="absolute inset-0 grid-bg" /></div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="admin-login-form">
          <img src="/brand/logo-sm.png" alt="E³ Impressão 3D" className="h-16" />
          <div><h1 className="text-2xl font-bold">{forgot ? "Recuperar senha" : "Administrativo"}</h1><p className="text-sm text-[#A0A0A0] mt-1">Acesso restrito à equipe E³.</p></div>
          <div><label className="label" htmlFor="ae">E-mail</label><input id="ae" type="email" required className="input" autoComplete="username" onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="admin-login-email" /></div>
          {!forgot && <div><label className="label" htmlFor="ap">Senha</label><input id="ap" type="password" required className="input" autoComplete="current-password" onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="admin-login-password" /></div>}
          <button className="btn-primary w-full py-3.5" disabled={busy} data-testid="admin-login-submit">{forgot ? "Enviar link" : busy ? "Entrando..." : "Entrar"}</button>
          <button type="button" className="text-sm text-[#A0A0A0] hover:text-white" onClick={() => setForgot(!forgot)} data-testid="admin-forgot-link">{forgot ? "Voltar ao login" : "Esqueci minha senha"}</button>
        </form>
      </div>
    </div>
  );
}
